<template>
  <div class="mobileViewStyle subscription-detail">
    <!-- 客户到期预警详情：查看模式（基本信息 + 续费记录）/ 编辑模式 / 续费模式 -->
    <div class="detail-body">
      <!-- ==================== 查看模式 ==================== -->
      <template v-if="isView">
        <list-card :label="detail.customerName || detail.subscriptionCode">
          <template #desc>
            <span class="status-tag" :class="'tag-' + statusTag(detail.warnExpireDate)">{{ statusOf(detail.warnExpireDate) }}</span>
            <span class="edit-btn" @click="toEdit">编辑</span>
          </template>
          <data-column label="编号">{{ detail.subscriptionCode || "—" }}</data-column>
          <data-column label="软件 / 版本">{{ softwareText(detail) }}</data-column>
          <data-column label="产品线">{{ detail.productLine || "—" }}</data-column>
          <data-column label="用户数">{{ detail.userCount ? detail.userCount + " 人" : "—" }}</data-column>
          <data-column label="销售 / 售后">{{ (detail.salesName || "—") + " / " + (detail.supportName || "—") }}</data-column>
          <data-column label="联系人">{{ (detail.contactPerson || "—") + " · " + (detail.contactPhone || "—") }}</data-column>
          <data-column label="购买 / 部署 / 年数">{{ (detail.purchaseType || "—") + " / " + (detail.deployCloud || "—") + " / " + (detail.serviceYears ? detail.serviceYears + " 年" : "—") }}</data-column>
          <data-column label="部署方式">{{ publishTypeText(detail.publishType) }}</data-column>
          <data-column label="开始">{{ dateOnly(detail.startDate) }}</data-column>
          <data-column label="服务到期">{{ dateOnly(detail.expireDate) }}</data-column>
          <data-column label="云服务器到期">{{ dateOnly(detail.cloudExpireDate) }}</data-column>
          <data-column label="预警基准到期">{{ dateOnly(detail.warnExpireDate) }}</data-column>
          <data-column label="到期状态">
            <span class="status-tag" :class="'tag-' + statusTag(detail.warnExpireDate)">{{ statusOf(detail.warnExpireDate) }}</span>
            <span class="remain" :style="{color: remainColor(detail.warnExpireDate)}">{{ remainText(detail.warnExpireDate) }}</span>
          </data-column>
          <data-column label="订阅金额 / 成本">{{ money(detail.amount) + " / " + money(detail.cost) }}</data-column>
          <data-column label="订阅毛利">{{ money(detail.profit) }}</data-column>
          <data-column label="续费金额 / 成本（默认）">{{ money(detail.renewAmount) + " / " + money(detail.renewCost) }}</data-column>
          <data-column label="续费毛利（默认）">{{ money(detail.renewProfit) }}</data-column>
          <data-column label="累计续费次数">{{ detail.renewTimes || 0 }}</data-column>
          <data-column v-if="detail.remark" label="备注">{{ detail.remark }}</data-column>
        </list-card>

        <!-- 续费记录：后端按续费时间倒序返回 -->
        <list-card v-if="renewLogList.length" :label="'续费记录（' + renewLogList.length + ' 次）'" type="form">
          <div class="renew-item" v-for="(log, index) in renewLogList" :key="log.id">
            <div class="renew-head">
              <span class="index">#{{ index + 1 }}</span>
              <span class="renew-tag" :class="isCloudRenewType(log.renewType) ? 'tag-warning' : 'tag-primary'">{{ renewTypeText(log.renewType) }}</span>
              <span class="time">{{ dateTime(log.renewTime) }}</span>
            </div>
            <data-column label="原到期">{{ dateOnly(log.fromDate) }}</data-column>
            <data-column label="新到期">{{ dateOnly(log.toDate) }}</data-column>
            <data-column label="续费金额">{{ money(log.amount) }}</data-column>
            <data-column label="成本">{{ money(log.cost) }}</data-column>
            <data-column label="毛利">{{ money(log.profit) }}</data-column>
            <data-column label="操作人">{{ log.operatorName || "—" }}</data-column>
          </div>
        </list-card>
        <div v-else class="renew-empty">暂无续费记录</div>
      </template>

      <!-- ==================== 编辑模式 ==================== -->
      <template v-else-if="isEdit">
        <list-card label="基础信息" type="form">
          <data-input label="客户">
            <mobile-search-select v-model="editData.customerId" :query="queryCustomerPage"
                                  label-column="merchantRemarkName" code-column="id"
                                  placeholder="请选择客户" search-placeholder="客户名称/手机号"
                                  :display-text="customerName" @change="customerName = ''">
              <!-- 客户名称下方补一行联系电话：同名客户最容易记混，电话能区分 -->
              <template #item="{item}">
                <div class="mss-item">
                  <div class="mss-item-main">{{ item.merchantRemarkName }}</div>
                  <div class="mss-item-sub">{{ item.merchantRemarkPhone || "无联系电话" }}</div>
                </div>
              </template>
            </mobile-search-select>
          </data-input>
          <data-input label="使用软件" v-model="editData.software" desc="" placeholder="如：中药材知识普及平台"/>
          <data-input label="版本号" v-model="editData.version" desc="" placeholder="如：V2.5 / 2025企业版"/>
          <data-input label="产品线">
            <mobile-select class="row-select" :clearable="false" v-model="editData.productLine" placeholder="请选择产品线">
              <mobile-option v-for="opt in productLineList" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="销售人员">
            <mobile-search-select v-model="editData.salesId" :query="queryEmployeePage"
                                  label-column="employeeName" code-column="id"
                                  placeholder="请选择销售人员" search-placeholder="职员名称/手机号"
                                  :display-text="salesName" @change="salesName = ''">
              <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
              <template #item="{item}">
                <div class="mss-item mss-item-avatar-row">
                  <div class="mss-item-avatar">
                    <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
                    <yq-icon v-else name="element-ui_user"/>
                  </div>
                  <div class="mss-item-info">
                    <div class="mss-item-main">{{ item.employeeName }}</div>
                    <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
                  </div>
                </div>
              </template>
            </mobile-search-select>
          </data-input>
          <data-input label="售后人员">
            <mobile-search-select v-model="editData.supportId" :query="queryEmployeePage"
                                  label-column="employeeName" code-column="id"
                                  placeholder="请选择售后人员" search-placeholder="职员名称/手机号"
                                  :display-text="supportName" @change="supportName = ''">
              <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
              <template #item="{item}">
                <div class="mss-item mss-item-avatar-row">
                  <div class="mss-item-avatar">
                    <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
                    <yq-icon v-else name="element-ui_user"/>
                  </div>
                  <div class="mss-item-info">
                    <div class="mss-item-main">{{ item.employeeName }}</div>
                    <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
                  </div>
                </div>
              </template>
            </mobile-search-select>
          </data-input>
          <data-input label="用户数" v-model="editData.userCount" desc="" placeholder="终端账号数"/>
          <data-input label="编号" v-model="editData.subscriptionCode" desc="" placeholder="留空自动生成"/>
        </list-card>

        <list-card label="商务信息" type="form">
          <data-input label="客户联系人" v-model="editData.contactPerson" desc="" placeholder="姓 + 称谓"/>
          <data-input label="客户电话" v-model="editData.contactPhone" desc="" placeholder="手机/座机"/>
          <data-input label="购买方式">
            <mobile-select class="row-select" :clearable="false" v-model="editData.purchaseType" placeholder="请选择购买方式">
              <mobile-option v-for="opt in purchaseTypeList" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="部署方式">
            <mobile-select class="row-select" :clearable="false" v-model="editData.publishType" placeholder="请选择部署方式" @change="onPublishTypeChange">
              <mobile-option v-for="opt in publishTypeList" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="部署云">
            <mobile-select class="row-select" :clearable="false" v-model="editData.deployCloud" placeholder="请选择部署云">
              <mobile-option v-for="opt in deployCloudList" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="服务年数" v-model="editData.serviceYears" desc="" placeholder="合同年"/>
          <data-input label="开始时间">
            <mobile-date-select v-model="editData.startDate" placeholder="请选择日期"/>
          </data-input>
          <data-input label="服务到期时间">
            <mobile-date-select v-model="editData.expireDate" placeholder="请选择日期"/>
          </data-input>
          <!-- 仅云服务器部署才要填（本地部署会清空，后端也会兜底置空） -->
          <data-input v-if="isCloudPublishEdit" label="云服务器到期">
            <mobile-date-select v-model="editData.cloudExpireDate" placeholder="请选择日期"/>
          </data-input>
          <data-input label="订阅金额(元)" v-model="editData.amount" desc="" placeholder="元/年，挂牌价"/>
          <data-input label="订阅成本(元)" v-model="editData.cost" desc="" placeholder="元/年"/>
          <data-input label="订阅毛利(元)">
            <span class="row-text" :style="{color: profitColor(editProfit)}">{{ money(editProfit) }}</span>
          </data-input>
          <data-input label="续费金额(元)" v-model="editData.renewAmount" desc="" placeholder="元/年"/>
          <data-input label="续费成本(元)" v-model="editData.renewCost" desc="" placeholder="元/年"/>
          <data-input label="续费毛利(元)">
            <span class="row-text" :style="{color: profitColor(editRenewProfit)}">{{ money(editRenewProfit) }}</span>
          </data-input>
          <data-input label="备注" v-model="editData.remark" desc="" placeholder="备注信息"/>
        </list-card>
      </template>

      <!-- ==================== 续费模式 ==================== -->
      <template v-else>
        <list-card label="续费" type="form">
          <data-input label="续费方式">
            <mobile-select class="row-select" :clearable="false" v-model="renewForm.renewType" placeholder="请选择续费方式" @change="applyRenewBase">
              <mobile-option v-for="opt in renewTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="续费年数" v-model="renewForm.years" desc="" placeholder="0"/>
          <data-input label="续费月数" v-model="renewForm.months" desc="" placeholder="0"/>
          <data-input label="续费金额(元)" v-model="renewForm.amount" desc="" placeholder="请输入续费金额"/>
          <data-input label="续费成本(元)" v-model="renewForm.cost" desc="" placeholder="请输入续费成本"/>
        </list-card>
        <!-- 顺延预览：原到期 -> 新到期 -->
        <div class="renew-preview" :class="{invalid: renewTotalMonth <= 0}">
          <div class="line">
            <span>原{{ renewBaseLabel }}</span>
            <b>{{ renewForm.baseExpireDate || "—" }}</b>
          </div>
          <div class="line">
            <span>新{{ renewBaseLabel }}</span>
            <b>{{ renewNextDate || "—" }}</b>
          </div>
          <div class="tip">
            {{ renewTotalMonth > 0 ? "共顺延 " + renewTotalMonth + " 个月" : "请填写续费时长，至少 1 个月" }}
          </div>
        </div>
        <div class="renew-hint">
          {{ isCloudRenew
            ? "💡 云服务器续费不取档案默认值，请手动填写本次金额 / 成本；留空则该条续费记录的金额成本为空"
            : "💡 续费金额 / 成本默认取档案的「续费金额 / 续费成本」，可在此修改" }}
        </div>
      </template>
    </div>

    <div class="detail-footer">
      <template v-if="isView">
        <mobile-button el-type="primary" @click="toRenew">续费</mobile-button>
      </template>
      <template v-else-if="isEdit">
        <mobile-button @click="cancelEdit">取消</mobile-button>
        <mobile-button el-type="primary" @click="saveData">保存</mobile-button>
      </template>
      <template v-else>
        <mobile-button @click="cancelRenew">取消</mobile-button>
        <mobile-button el-type="primary" @click="doRenew">确认续费</mobile-button>
      </template>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileDateSelect from "@/components/qywx/data/MobileDateSelect.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import subscriptionApi from "@/api/dz-crm/partner/subscriptionApi";
import commonDataApi from "@/api/normal/common/commonDataApi";
import commonApi from "@/api/commonApi";
import {PARTNER_TYPE} from "@/views/h5/crm/crmDictionary";
import {
  PUBLISH_TYPE, RENEW_TYPE, isCloudPublish, publishTypeText, renewTypeText,
  statusOf, statusTag, remainText, remainColor, dateOnly, dateTime, addMonths,
  productLineOptions, purchaseTypeOptions, deployCloudOptions, publishTypeOptions
} from "@/views/h5/crm/subscriptionDictionary";

/** 取日期部分，取不到返回空串（表单里空串表示未选，不能返回 "—"） */
function dateValue(value) {
  return value ? String(value).slice(0, 10) : "";
}

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "CSD",
  components: {
    MobileButton, MobileSearchSelect, MobileDateSelect, MobileOption, MobileSelect, DataColumn, DataInput, ListCard
  },
  data() {
    return {
      mode: "view",  // view（查看）/ edit（编辑，新建时也是它）/ renew（续费）
      detail: {},
      editData: {},
      customerName: "",
      salesName: "",
      supportName: "",
      renewForm: {
        id: "",
        publishType: null,
        renewType: RENEW_TYPE.SERVICE,
        serviceExpireDate: "",
        cloudExpireDate: "",
        baseExpireDate: "",
        years: 1,
        months: 0,
        amount: 0,
        cost: 0,
        // 档案里的默认续费金额/成本：切回服务续费时用来还原
        renewAmount: 0,
        renewCost: 0,
      },
      productLineList: productLineOptions,
      purchaseTypeList: purchaseTypeOptions,
      deployCloudList: deployCloudOptions,
      publishTypeList: publishTypeOptions,
      pageTitle: "",
    };
  },
  computed: {
    isView() {
      return this.mode === "view";
    },
    isEdit() {
      return this.mode === "edit";
    },
    renewLogList() {
      return this.detail.renewLogList || [];
    },
    // 编辑态是否云服务器部署（决定「云服务器到期」这一行渲染与否）
    isCloudPublishEdit() {
      return isCloudPublish(this.editData.publishType);
    },
    /** 订阅毛利 = 订阅金额 - 订阅成本（只读，自动算） */
    editProfit() {
      return subtract(this.editData.amount, this.editData.cost);
    },
    /** 续费毛利 = 续费金额 - 续费成本（只读，自动算） */
    editRenewProfit() {
      return subtract(this.editData.renewAmount, this.editData.renewCost);
    },
    // 本次是否云服务器续费
    isCloudRenew() {
      return Number(this.renewForm.renewType) === RENEW_TYPE.CLOUD;
    },
    // 本地部署只能服务续费，云服务器部署才多一个「云服务器续费」
    renewTypeOptions() {
      const list = [{label: "服务续费（顺延服务到期）", value: RENEW_TYPE.SERVICE}];
      if (isCloudPublish(this.detail.publishType)) {
        list.push({label: "云服务器续费（顺延云服务器到期）", value: RENEW_TYPE.CLOUD});
      }
      return list;
    },
    renewBaseLabel() {
      return this.isCloudRenew ? "云服务器到期时间" : "服务到期时间";
    },
    renewTotalMonth() {
      return (Number(this.renewForm.years) || 0) * 12 + (Number(this.renewForm.months) || 0);
    },
    // 顺延后的新到期日（保日，与后端 addMonthsKeepDay 同口径）
    renewNextDate() {
      return addMonths(this.renewForm.baseExpireDate, this.renewForm.years, this.renewForm.months);
    }
  },
  created() {
    this.pageTitle = document.title;
    document.title = this.route.query.id ? "到期预警详情" : "新增订阅";
    if (this.route.query.id) {
      // 点列表进来 = 查看模式
      this.mode = "view";
      this.findById();
    } else {
      // 新建 = 编辑模式
      this.mode = "edit";
      this.initNewSubscription();
    }
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    findById() {
      subscriptionApi.findById(this.route.query.id).then(res => {
        this.detail = res || {};
      });
    },
    // 到期档相关（口径见 subscriptionDictionary）
    statusOf(warnExpireDate) {
      return statusOf(warnExpireDate);
    },
    statusTag(warnExpireDate) {
      return statusTag(warnExpireDate);
    },
    remainText(warnExpireDate) {
      return remainText(warnExpireDate);
    },
    remainColor(warnExpireDate) {
      return remainColor(warnExpireDate);
    },
    dateOnly(value) {
      return dateOnly(value);
    },
    dateTime(value) {
      return dateTime(value);
    },
    publishTypeText(publishType) {
      return publishTypeText(publishType);
    },
    renewTypeText(renewType) {
      return renewTypeText(renewType);
    },
    isCloudRenewType(renewType) {
      return Number(renewType) === RENEW_TYPE.CLOUD;
    },
    softwareText(item) {
      const text = item.software || "—";
      return item.version ? text + " · " + item.version : text;
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return "¥ " + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    /** 毛利为负标红 */
    profitColor(value) {
      return (Number(value) || 0) < 0 ? "#c0392b" : "#2e8b57";
    },
    // 客户 / 职员选择框的数据源
    queryCustomerPage(params) {
      return commonDataApi.queryPartnerList({...params, partnerType: PARTNER_TYPE.CUSTOMER});
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    // ---------- 编辑 ----------
    // 查看模式 -> 编辑模式
    toEdit() {
      this.editData = JSON.parse(JSON.stringify(this.detail));
      // 日期字段归一为 yyyy-MM-dd（后端返回可能带时间）
      this.editData.startDate = dateValue(this.editData.startDate);
      this.editData.expireDate = dateValue(this.editData.expireDate);
      this.editData.cloudExpireDate = dateValue(this.editData.cloudExpireDate);
      this.customerName = this.editData.customerName || "";
      this.salesName = this.editData.salesName || "";
      this.supportName = this.editData.supportName || "";
      this.mode = "edit";
    },
    // 新建订阅：初始化一份空表单（默认云服务器部署，与 web 端一致）
    initNewSubscription() {
      this.editData = {
        id: "",
        subscriptionCode: "",
        customerId: "",
        software: "",
        version: "",
        productLine: "",
        salesId: "",
        supportId: "",
        contactPerson: "",
        contactPhone: "",
        userCount: 0,
        purchaseType: "",
        deployCloud: "",
        serviceYears: 1,
        publishType: PUBLISH_TYPE.CLOUD,
        startDate: "",
        expireDate: "",
        cloudExpireDate: "",
        amount: 0,
        cost: 0,
        renewAmount: 0,
        renewCost: 0,
        remark: "",
      };
      this.customerName = "";
      this.salesName = "";
      this.supportName = "";
    },
    // 取消编辑：已有订阅回查看模式（重新拉一次，丢弃未保存的改动）；新建则回列表
    cancelEdit() {
      if (this.route.query.id) {
        this.mode = "view";
        this.findById();
      } else {
        this.router.back();
      }
    },
    // 切换部署方式：本地部署清空云服务器到期时间（后端 save 也会兜底置空）
    onPublishTypeChange(value) {
      if (!isCloudPublish(value)) {
        this.editData.cloudExpireDate = "";
      }
    },
    saveData() {
      if (!this.editData.customerId) {
        this.$message.error("请选择客户");
        return;
      }
      if (!this.editData.software) {
        this.$message.error("请填写软件名");
        return;
      }
      if (!this.editData.publishType) {
        this.$message.error("请选择部署方式");
        return;
      }
      if (!this.editData.expireDate) {
        this.$message.error("请填写服务到期时间");
        return;
      }
      if (this.isCloudPublishEdit && !this.editData.cloudExpireDate) {
        this.$message.error("云服务器部署必须填写云服务器到期时间");
        return;
      }
      const isNew = !this.route.query.id;
      const payload = JSON.parse(JSON.stringify(this.editData));
      // 毛利后端会按 金额-成本 兜底重算，这里一并传保证即时一致
      payload.profit = this.editProfit;
      payload.renewProfit = this.editRenewProfit;
      ["amount", "cost", "profit", "renewAmount", "renewCost", "renewProfit", "userCount", "serviceYears", "publishType"].forEach(k => {
        payload[k] = Number(payload[k]) || 0;
      });
      // 本地部署不允许写入云服务器到期时间（后端也会兜底置空）
      if (!this.isCloudPublishEdit) {
        payload.cloudExpireDate = null;
      }
      // 成功提示由请求拦截器统一弹
      subscriptionApi.save(payload).then(() => {
        if (isNew) {
          // 新建：回列表
          this.router.back();
        } else {
          // 编辑已有订阅：回查看模式并刷新
          this.mode = "view";
          this.findById();
        }
      });
    },
    // ---------- 续费 ----------
    toRenew() {
      this.renewForm = {
        id: this.detail.id,
        publishType: this.detail.publishType,
        renewType: RENEW_TYPE.SERVICE,
        serviceExpireDate: dateValue(this.detail.expireDate),
        cloudExpireDate: dateValue(this.detail.cloudExpireDate),
        baseExpireDate: "",
        // 默认续 1 年，与 web 端一致
        years: 1,
        months: 0,
        amount: 0,
        cost: 0,
        renewAmount: this.detail.renewAmount,
        renewCost: this.detail.renewCost,
      };
      this.applyRenewBase();
      this.mode = "renew";
    },
    cancelRenew() {
      this.mode = "view";
    },
    /** 按当前续费方式重设基准到期日、本次金额/成本 */
    applyRenewBase() {
      const form = this.renewForm;
      // 先归一续费方式：本地部署只能服务续费，避免下面按错误的基准日取值
      if (!isCloudPublish(form.publishType)) {
        form.renewType = RENEW_TYPE.SERVICE;
      }
      if (this.isCloudRenew) {
        form.baseExpireDate = form.cloudExpireDate || "";
        // 云服务器续费的金额/成本不取档案默认值，由操作人手动填
        form.amount = null;
        form.cost = null;
      } else {
        form.baseExpireDate = form.serviceExpireDate || "";
        form.amount = Number(form.renewAmount) || 0;
        form.cost = Number(form.renewCost) || 0;
      }
    },
    doRenew() {
      if (this.renewTotalMonth <= 0 || !this.renewForm.baseExpireDate) {
        this.$message.error("续费时长至少 1 个月");
        return;
      }
      const payload = {
        id: this.renewForm.id,
        renewType: this.renewForm.renewType,
        years: Number(this.renewForm.years) || 0,
        months: Number(this.renewForm.months) || 0,
        // 服务续费：金额/成本 0 或不填表示沿用档案默认；云服务器续费：手动填写，不填则记为空
        amount: Number(this.renewForm.amount) > 0 ? this.renewForm.amount : null,
        cost: Number(this.renewForm.cost) > 0 ? this.renewForm.cost : null,
      };
      // renew 不是 save 接口，拦截器不弹提示，这里手动提示新到期日
      subscriptionApi.renew(payload).then(res => {
        const newExpire = this.isCloudRenew ? res.cloudExpireDate : res.expireDate;
        this.$message.success("已续费，新" + this.renewBaseLabel + "：" + dateOnly(newExpire));
        this.mode = "view";
        this.findById();
      });
    }
  }
};

/** 金额相减（空值按 0），保留 2 位 */
function subtract(a, b) {
  const result = (Number(a) || 0) - (Number(b) || 0);
  return Math.round(result * 100) / 100;
}
</script>

<style lang="scss" scoped>
.subscription-detail{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  // 标题行右侧（查看模式）：到期档标签 + 编辑按钮
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .edit-btn{
    flex: none;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.tag-primary{
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
    }
    &.tag-info{
      color: var(--el-color-info);
      background: var(--el-fill-color-light);
    }
    &.tag-warning{
      color: var(--el-color-warning);
      background: var(--el-color-warning-light-9);
    }
    &.tag-success{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
    &.tag-danger{
      color: var(--el-color-danger);
      background: var(--el-color-danger-light-9);
    }
  }
  .remain{
    margin-left: 6px;
  }
  .row-text{
    color: var(--yq-font-color-base);
  }
  // 续费记录
  .renew-item{
    padding: 10px 0;
    border-bottom: 1px solid var(--yq-border-color);
    &:first-child{
      padding-top: 0;
    }
    &:last-child{
      border-bottom: none;
      padding-bottom: 0;
    }
    .renew-head{
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: var(--yq-font-size-mini-mobile);
      .index{
        color: var(--yq-font-color-desc);
      }
      .time{
        margin-left: auto;
        color: var(--yq-font-color-desc);
      }
    }
    .renew-tag{
      flex: none;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: var(--yq-font-size-mini-mobile);
      &.tag-primary{
        color: var(--el-color-primary);
        background: var(--el-color-primary-light-9);
      }
      &.tag-warning{
        color: var(--el-color-warning);
        background: var(--el-color-warning-light-9);
      }
    }
  }
  .renew-empty{
    margin-top: 10px;
    padding: 20px;
    text-align: center;
    color: var(--yq-font-color-desc);
    background: #FFF;
    border-radius: 6px;
    box-shadow: 0 0 10px var(--yq-shadow);
  }
  // 顺延预览
  .renew-preview{
    margin-top: 10px;
    padding: 12px;
    border-radius: 6px;
    background: #FFF;
    box-shadow: 0 0 10px var(--yq-shadow);
    .line{
      display: flex;
      justify-content: space-between;
      line-height: 2;
      span{
        color: var(--yq-font-color-desc);
      }
    }
    .tip{
      margin-top: 4px;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--el-color-success);
    }
    &.invalid .tip{
      color: var(--el-color-danger);
    }
  }
  .renew-hint{
    margin-top: 10px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--yq-font-color-desc);
    line-height: 1.6;
  }
  // 选择类控件放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 240px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
}
</style>
